import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import {
  buildPaymentEntryLabel,
  formatDescontoExibicao,
} from "../../utils/showcase-config-helpers";

export default function PaymentConditionsSection({
  brandId,
  paymentOptions,
  loadingPaymentConditions,
  paymentConditions,
  onChange,
}) {
  const [draftCondition, setDraftCondition] = useState(null);
  const [draftDiscount, setDraftDiscount] = useState("");
  const [draftError, setDraftError] = useState("");

  const editMode = useSelector((state) => state.criarVitrine.editMode);
  const originalStatus = useSelector(
    (state) => state.criarVitrine.originalStatus,
  );
  const originalPaymentConditionIds = useSelector(
    (state) => state.criarVitrine.originalPaymentConditionIds,
  );
  const lockedIds = useMemo(() => {
    if (!editMode || originalStatus !== "PUBLICADO") return new Set();
    return new Set(originalPaymentConditionIds);
  }, [editMode, originalStatus, originalPaymentConditionIds]);

  const addedIds = useMemo(
    () => new Set((paymentConditions ?? []).map((c) => c.IdCondicaoPagamento)),
    [paymentConditions],
  );

  const availableOptions = useMemo(
    () =>
      (paymentOptions ?? []).filter(
        (opt) => !addedIds.has(opt.IdCondicaoPagamento),
      ),
    [paymentOptions, addedIds],
  );

  const handleAdd = () => {
    if (!draftCondition?.IdCondicaoPagamento) {
      setDraftError("Selecione uma condição de pagamento.");
      return;
    }

    const discountValue =
      draftDiscount === "" || draftDiscount == null
        ? null
        : Number(String(draftDiscount).replace(",", "."));

    if (
      discountValue == null ||
      Number.isNaN(discountValue) ||
      discountValue < 0
    ) {
      setDraftError("Informe o desconto (%) para esta condição.");
      return;
    }

    const entry = {
      ...draftCondition,
      PercentualDesconto: discountValue,
      displayLabel: buildPaymentEntryLabel({
        ...draftCondition,
        PercentualDesconto: discountValue,
      }),
    };

    onChange([...(paymentConditions ?? []), entry]);
    setDraftCondition(null);
    setDraftDiscount("");
    setDraftError("");
  };

  const handleRemove = (idCondicao) => {
    if (lockedIds.has(idCondicao)) return;
    onChange(
      (paymentConditions ?? []).filter(
        (c) => c.IdCondicaoPagamento !== idCondicao,
      ),
    );
  };

  const handleSelectCondition = (option) => {
    setDraftCondition(option);
    setDraftDiscount("");
    setDraftError("");
  };

  return (
    <>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ width: "100%" }}>
            <AutoCompleteField
              options={availableOptions}
              label="Condição de pagamento"
              optionLabelKey="displayLabel"
              optionIdKey="IdCondicaoPagamento"
              onChange={handleSelectCondition}
              value={draftCondition}
              disabled={!brandId || loadingPaymentConditions}
              loading={loadingPaymentConditions}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Box sx={{ width: "100%" }}>
            <InputText
              label="Desconto (%) *"
              placeholder="Ex.: 5"
              type="number"
              value={draftDiscount}
              onChange={(e) => {
                setDraftDiscount(e.target.value);
                setDraftError("");
              }}
              className="form-control"
              style={{ width: "100%" }}
              disabled={!brandId}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant="contained"
            style={{
              display: "flex",
              justifyContent: "center",
              backgroundColor: themeColorsDesingSystem.primary.secondary,
            }}
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={!brandId || loadingPaymentConditions}
          >
            Adicionar
          </Button>
        </Grid>
      </Grid>

      {draftError ? (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "#d32f2f" }}
        >
          {draftError}
        </Typography>
      ) : null}

      {(paymentConditions?.length ?? 0) > 0 ? (
        <Box
          mt={2}
          sx={{
            border: `1px solid ${themeColorsDesingSystem.stone.main}`,
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <List dense disablePadding>
            {paymentConditions.map((entry, index) => {
              const locked = lockedIds.has(entry.IdCondicaoPagamento);
              const removeButton = (
                <IconButton
                  edge="end"
                  aria-label="Remover condição"
                  onClick={() => handleRemove(entry.IdCondicaoPagamento)}
                  size="small"
                  disabled={locked}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              );
              return (
                <ListItem
                  key={entry.IdCondicaoPagamento}
                  divider={index < paymentConditions.length - 1}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemText
                    primary={
                      entry.Politica?.trim() ||
                      `Condição ${entry.IdCondicaoPagamento}`
                    }
                    secondary={`Desconto: ${formatDescontoExibicao(entry.PercentualDesconto)}${
                      entry.PrazoMedio ? ` · Prazo: ${entry.PrazoMedio} dias` : ""
                    }`}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  <ListItemSecondaryAction>
                    {locked ? (
                      <Tooltip title="Não é possível remover condição de pagamento de vitrine já publicada">
                        <span>{removeButton}</span>
                      </Tooltip>
                    ) : (
                      removeButton
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{ mt: 2, color: themeColorsDesingSystem.gray.dark }}
        >
          Nenhuma condição adicionada. Selecione a condição, informe o desconto
          e clique em Adicionar.
        </Typography>
      )}
    </>
  );
}
